import React from 'react';
import { Download, FileText, Table } from 'lucide-react';
import { jsPDF } from 'jspdf';
import CurrencyService from '../../../services/currencyService';
import { useActivityLog } from '../../../hooks/useActivityLog';
import formatDate from '../utils/dateFormatter';

const ReportsPage = ({ 
    selectedCompany, 
    companies, 
    shareholders, 
    directors, 
    shareClasses, 
    issuances, 
    addError 
}) => {
    // Log page access
    useActivityLog('Reports', selectedCompany?.id);
    
    const getShareholderName = (shareholder) => {
        if (shareholder.entity_type === 'Individual') {
            const { first_name, middle_name, last_name } = shareholder;
            const names = [first_name, middle_name, last_name].filter(name => name && name.trim() !== '');
            return names.join(' ');
        } else {
            return shareholder.name || 'Unknown';
        }
    };

    const getDirectorName = (director) => {
        const { first_name, middle_name, last_name } = director;
        const names = [first_name, middle_name, last_name].filter(name => name && name.trim() !== '');
        return names.join(' ');
    };

    const getDirectorTypes = (director) => {
        const types = [];
        if (director.is_director) types.push('Director');
        if (director.is_alternate_director) types.push('Alternate Director');
        if (director.is_company_secretary) types.push('Company Secretary');
        return types.join(', ');
    };

    const generateHtmlReport = () => {
        try {
            const reportHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Company Profile Report - ${selectedCompany.name}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
        .header { text-align: center; margin-bottom: 40px; }
        .company-name { font-size: 24px; font-weight: bold; color: #1a73e8; margin-bottom: 10px; }
        .report-title { font-size: 20px; margin-bottom: 20px; }
        .section { margin-bottom: 30px; }
        .section-title { font-size: 18px; font-weight: bold; color: #1a73e8; border-bottom: 2px solid #1a73e8; padding-bottom: 5px; margin-bottom: 15px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #1a73e8; color: white; }
        tr:nth-child(even) { background-color: #f9f9f9; }
        .info-grid { display: grid; grid-template-columns: 1fr 3fr; gap: 10px; margin-bottom: 15px; }
        .info-label { font-weight: bold; }
        .footer { text-align: center; margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
        @media print {
            body { margin: 20px; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="report-title">Company Profile Report</div>
        <div class="company-name">${selectedCompany.name}</div>
    </div>
    <div class="section">
        <div class="section-title">Company Information</div>
        <div class="info-grid">
            <div class="info-label">Name:</div>
            <div>${selectedCompany.name}</div>
            <div class="info-label">Description:</div>
            <div>${selectedCompany.description || 'N/A'}</div>
            ${selectedCompany.address ? `
            <div class="info-label">Address:</div>
            <div>${selectedCompany.address.line1 || ''}, ${selectedCompany.address.suburb || ''}, ${selectedCompany.address.state || ''}, ${selectedCompany.address.postcode || ''}, ${selectedCompany.address.country || ''}</div>
            ` : ''}
            <div class="info-label">Report Generated:</div>
            <div>${formatDate(new Date())}</div>
        </div>
    </div>
    ${directors && directors.length > 0 ? `
    <div class="section">
        <div class="section-title">Directors</div>
        <table>
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Email</th>
                    <th>Date Appointed</th>
                    <th>Date End</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                ${directors.map(director => `
                    <tr>
                        <td>${getDirectorName(director)}</td>
                        <td>${getDirectorTypes(director)}</td>
                        <td>${director.email || 'N/A'}</td>
                        <td>${formatDate(director.date_appointed)}</td>
                        <td>${director.date_end ? formatDate(director.date_end) : 'Current'}</td>
                        <td>${director.status}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    </div>
    ` : ''}
    ${shareholders && shareholders.length > 0 ? `
    <div class="section">
        <div class="section-title">Shareholders Summary</div>
        <table>
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Entity Type</th>
                    <th>Shareholder Type</th>
                    <th>Email</th>
                </tr>
            </thead>
            <tbody>
                ${shareholders.map(shareholder => `
                    <tr>
                        <td>${getShareholderName(shareholder)}</td>
                        <td>${shareholder.entity_type || 'N/A'}</td>
                        <td>${shareholder.type || 'N/A'}</td>
                        <td>${shareholder.email || 'N/A'}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    </div>
    ` : ''}
    ${shareClasses && shareClasses.length > 0 ? `
    <div class="section">
        <div class="section-title">Share Classes</div>
        <table>
            <thead>
                <tr>
                    <th>Share Class</th>
                    <th>Description</th>
                </tr>
            </thead>
            <tbody>
                ${shareClasses.map(shareClass => `
                    <tr>
                        <td>${shareClass.name || 'N/A'}</td>
                        <td>${shareClass.description || 'N/A'}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    </div>
    ` : ''}
    ${issuances && issuances.length > 0 ? `
    <div class="section">
        <div class="section-title">Share Issuances Summary</div>
        <div class="info-grid">
            <div class="info-label">Total Shares Issued:</div>
            <div>${issuances.reduce((sum, issuance) => sum + (issuance.shares || 0), 0).toLocaleString()}</div>
            <div class="info-label">Total Issued Capital:</div>
            <div>{CurrencyService.formatAmount(issuances.reduce((sum, issuance) => sum + ((issuance.shares || 0) * (issuance.price_per_share || 0)), 0), selectedCompany?.currency || 'USD')}</div>
            <div class="info-label">Number of Issuances:</div>
            <div>${issuances.length}</div>
        </div>
    </div>
    ` : ''}
    <div class="footer">
        <div>Created by Kapitalized Equity Manager</div>
        <div>kapitalized.com</div>
    </div>
    <div class="no-print" style="position: fixed; top: 20px; right: 20px;">
        <button onclick="window.print()" style="background: #1a73e8; color: white; padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer;">Print/Save as PDF</button>
    </div>
</body>
</html>
            `;

            const newWindow = window.open('', '_blank');
            newWindow.document.write(reportHtml);
            newWindow.document.close();
            
            addError('Company profile report opened in new window. Use your browser\'s Print function to save as PDF.');
            
        } catch (error) {
            console.error('Error generating HTML report:', error);
            addError(`Error generating report: ${error.message}`);
        }
    };

    const generateCompanyProfilePDF = async () => {
        if (!selectedCompany) {
            addError('Please select a company first.');
            return;
        }

        try {
            // Use the imported jsPDF directly
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            let yPosition = 20;

            // Helper function to add text with automatic page break
            const addText = (text, x, y, options = {}) => {
                if (y > pageHeight - 30) {
                    doc.addPage();
                    y = 20;
                }
                const textStr = String(text || '');
                const maxWidth = options.maxWidth || (pageWidth - 40);
                
                const lines = doc.splitTextToSize(textStr, maxWidth);
                lines.forEach((line, index) => {
                    if (y + (index * 5) > pageHeight - 30) {
                        doc.addPage();
                        y = 20 - (index * 5); // Adjust y on new page
                    }
                    doc.text(line, x, y + (index * 5), options);
                });
                return y + (lines.length * 5);
            };

            // Helper function to add simple table
            const addSimpleTable = (headers, rows, startY) => {
                let currentY = startY;
                
                if (currentY > pageHeight - 60) {
                    doc.addPage();
                    currentY = 20;
                }
                
                doc.setFontSize(8);
                doc.setFont(undefined, 'bold');
                const colWidth = (pageWidth - 40) / headers.length;
                
                headers.forEach((header, index) => {
                    doc.text(String(header), 20 + (index * colWidth), currentY);
                });
                currentY += 7;
                
                doc.setFont(undefined, 'normal');
                rows.forEach((row) => {
                    if (currentY > pageHeight - 20) {
                        doc.addPage();
                        currentY = 20;
                    }
                    
                    let maxRowHeight = 0;
                    row.forEach((cell, index) => {
                        const cellText = String(cell || '');
                        const lines = doc.splitTextToSize(cellText, colWidth - 2);
                        doc.text(lines, 20 + (index * colWidth), currentY);
                        if (lines.length * 5 > maxRowHeight) {
                            maxRowHeight = lines.length * 5;
                        }
                    });
                    currentY += maxRowHeight + 2;
                });
                
                return currentY + 5;
            };

            // Title
            doc.setFontSize(20);
            doc.setFont(undefined, 'bold');
            yPosition = addText('Company Profile Report', pageWidth / 2, yPosition, { align: 'center' });
            
            yPosition += 10;
            doc.setFontSize(14);
            yPosition = addText(selectedCompany.name, pageWidth / 2, yPosition, { align: 'center' });

            // Company Information
            yPosition += 15;
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            yPosition = addText('Company Information', 20, yPosition);
            
            yPosition += 7;
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            yPosition = addText(`Name: ${selectedCompany.name}`, 20, yPosition);
            yPosition = addText(`Description: ${selectedCompany.description || 'N/A'}`, 20, yPosition);
            
            if (selectedCompany.address) {
                const address = selectedCompany.address;
                const addressLine = `${address.line1 || ''}, ${address.suburb || ''}, ${address.state || ''}, ${address.postcode || ''}, ${address.country || ''}`;
                yPosition = addText(`Address: ${addressLine}`, 20, yPosition);
            }

            yPosition = addText(`Report Generated: ${formatDate(new Date())}`, 20, yPosition);

            // Directors Section
            if (directors && directors.length > 0) {
                yPosition += 10;
                doc.setFontSize(16);
                doc.setFont(undefined, 'bold');
                yPosition = addText('Directors', 20, yPosition);

                const directorRows = directors.map(director => [
                    getDirectorName(director),
                    getDirectorTypes(director),
                    director.email || 'N/A',
                    formatDate(director.date_appointed),
                    director.date_end ? formatDate(director.date_end) : 'Current',
                    director.status
                ]);

                yPosition = addSimpleTable(
                    ['Name', 'Type', 'Email', 'Appointed', 'End Date', 'Status'],
                    directorRows,
                    yPosition + 5
                );
            }

            // Shareholders Summary
            if (shareholders && shareholders.length > 0) {
                yPosition += 10;
                doc.setFontSize(16);
                doc.setFont(undefined, 'bold');
                yPosition = addText('Shareholders Summary', 20, yPosition);

                const shareholderRows = shareholders.map(shareholder => [
                    getShareholderName(shareholder),
                    shareholder.entity_type || 'N/A',
                    shareholder.type || 'N/A',
                    shareholder.email || 'N/A'
                ]);

                yPosition = addSimpleTable(
                    ['Name', 'Entity Type', 'Type', 'Email'],
                    shareholderRows,
                    yPosition + 5
                );
            }

            // Share Classes
            if (shareClasses && shareClasses.length > 0) {
                yPosition += 10;
                doc.setFontSize(16);
                doc.setFont(undefined, 'bold');
                yPosition = addText('Share Classes', 20, yPosition);

                const shareClassRows = shareClasses.map(shareClass => [
                    shareClass.name || 'N/A',
                    shareClass.description || 'N/A'
                ]);

                yPosition = addSimpleTable(
                    ['Share Class', 'Description'],
                    shareClassRows,
                    yPosition + 5
                );
            }

            // Share Issuances Summary
            if (issuances && issuances.length > 0) {
                const totalShares = issuances.reduce((sum, issuance) => sum + (issuance.shares || 0), 0);
                const totalValue = issuances.reduce((sum, issuance) => sum + ((issuance.shares || 0) * (issuance.price_per_share || 0)), 0);

                yPosition += 15;
                doc.setFontSize(16);
                doc.setFont(undefined, 'bold');
                yPosition = addText('Share Issuances Summary', 20, yPosition);
                
                yPosition += 7;
                doc.setFontSize(10);
                doc.setFont(undefined, 'normal');
                yPosition = addText(`Total Shares Issued: ${totalShares.toLocaleString()}`, 20, yPosition);
                yPosition = addText(`Total Issued Capital: ${CurrencyService.formatAmount(totalValue, selectedCompany?.currency || 'USD')}`, 20, yPosition);
                addText(`Number of Issuances: ${issuances.length}`, 20, yPosition);
            }

            // Footer on each page
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setFont(undefined, 'normal');
                doc.text('Created by Kapitalized Equity Manager', pageWidth/2, pageHeight - 20, { align: 'center' });
                doc.text('kapitalized.com', pageWidth/2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${i}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
            }

            // Save the PDF
            const fileName = `${selectedCompany.name.replace(/[^a-z0-9]/gi, '_')}_Company_Profile.pdf`;
            doc.save(fileName);
            
            addError('Company profile PDF generated successfully!');
            
        } catch (error) {
            console.error('Error generating PDF:', error);
            addError(`Error generating PDF: ${error.message}. Falling back to HTML report.`);
            // Fallback to HTML report if PDF generation fails
            generateHtmlReport();
        }
    };

    const generateShareholdingsCSV = () => {
        if (!selectedCompany) {
            addError('Please select a company first.');
            return;
        }

        if (!issuances || issuances.length === 0) {
            addError('No share issuances found for this company.');
            return;
        }

        try {
            const headers = [
                'Shareholder Name', 'Entity Type', 'Shareholder Type', 'Email',
                'Share Class', 'Shares', 'Price per Share', 'Issued Capital',
                'Issue Date', 'Round Number', 'Round Title', 'Payment Status'
            ];

            const csvData = issuances.map(issuance => {
                const shareholder = shareholders.find(s => s.id === issuance.shareholder_id);
                const shareClass = shareClasses.find(sc => sc.id === issuance.share_class_id);
                
                return [
                    shareholder ? getShareholderName(shareholder) : 'Unknown',
                    shareholder?.entity_type || 'N/A',
                    shareholder?.type || 'N/A',
                    shareholder?.email || 'N/A',
                    shareClass?.name || 'Unknown',
                    issuance.shares || 0,
                    issuance.price_per_share || 0,
                    (issuance.shares || 0) * (issuance.price_per_share || 0),
                    issuance.issue_date || 'N/A',
                    issuance.round_number || 'N/A',
                    issuance.round_title || 'N/A',
                    issuance.payment_status || 'N/A'
                ];
            });

            const csvContent = [headers, ...csvData]
                .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${selectedCompany.name.replace(/[^a-z0-9]/gi, '_')}_Shareholdings.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            addError('Shareholdings CSV downloaded successfully!');
        } catch (error) {
            console.error('Error generating CSV:', error);
            addError(`Error generating CSV: ${error.message}`);
        }
    };

    if (!selectedCompany) {
        return (
            <div className="space-y-6">
                <h2 className="text-2xl font-bold">Reports</h2>
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    Please select a company to generate reports.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Reports</h2>
                <span className="text-sm text-gray-600">Company: {selectedCompany.name}</span>
            </div>
            
            <p className="text-gray-600">Generate comprehensive reports for your company's equity structure and shareholdings.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center mb-4">
                        <FileText className="h-8 w-8 text-blue-600 mr-3" />
                        <div>
                            <h3 className="text-xl font-bold">Company Profile Report</h3>
                            <p className="text-sm text-gray-500">1-2 page comprehensive report</p>
                        </div>
                    </div>
                    <p className="mb-4 text-gray-600">
                        Generate a detailed report including company information, directors, 
                        shareholders summary, share classes, and issuance overview. Perfect for 
                        board meetings, investor presentations, or regulatory filings.
                    </p>
                    <div className="mb-4 text-sm text-gray-500">
                        <strong>Includes:</strong>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                            <li>Company information & address</li>
                            <li>Directors list with appointments</li>
                            <li>Shareholders summary by type</li>
                            <li>Share classes breakdown</li>
                            <li>Total shares and issued capital summary</li>
                        </ul>
                    </div>
                    <button
                        onClick={generateCompanyProfilePDF}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center"
                    >
                        <Download className="h-5 w-5 mr-2" />
                        Generate Company Profile Report
                    </button>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center mb-4">
                        <Table className="h-8 w-8 text-green-600 mr-3" />
                        <div>
                            <h3 className="text-xl font-bold">Shareholdings Export (CSV)</h3>
                            <p className="text-sm text-gray-500">Detailed spreadsheet format</p>
                        </div>
                    </div>
                    <p className="mb-4 text-gray-600">
                        Export a comprehensive CSV file containing all shareholdings data. 
                        Perfect for detailed analysis, accounting integration, or creating 
                        custom reports in Excel or Google Sheets.
                    </p>
                    <div className="mb-4 text-sm text-gray-500">
                        <strong>Includes:</strong>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                            <li>Shareholder details & contact info</li>
                            <li>Share class and quantity details</li>
                            <li>Pricing and valuation data</li>
                            <li>Issue dates and round information</li>
                            <li>Payment status tracking</li>
                        </ul>
                    </div>
                    <button
                        onClick={generateShareholdingsCSV}
                        className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center justify-center"
                    >
                        <Download className="h-5 w-5 mr-2" />
                        Download Shareholdings CSV
                    </button>
                </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Report Statistics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                        <span className="text-gray-600">Directors:</span>
                        <span className="font-medium ml-2">{directors?.length || 0}</span>
                    </div>
                    <div>
                        <span className="text-gray-600">Shareholders:</span>
                        <span className="font-medium ml-2">{shareholders?.length || 0}</span>
                    </div>
                    <div>
                        <span className="text-gray-600">Share Classes:</span>
                        <span className="font-medium ml-2">{shareClasses?.length || 0}</span>
                    </div>
                    <div>
                        <span className="text-gray-600">Issuances:</span>
                        <span className="font-medium ml-2">{issuances?.length || 0}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportsPage;